import { newIdempotencyKey } from "../lib/idempotency";
import { readStorage, removeStorage, writeStorage } from "../lib/storage";
import { ApiError, kindFor } from "./errors";

// The one fetch wrapper. Responsibilities:
//
//  * Same-origin only (credentials: "same-origin"): the session is the
//    httpOnly vads_session cookie set by POST /v1/auth/token
//    (internal/auth/session_middleware.go:115-125); JS never sees it.
//  * CSRF double-submit: every non-GET/HEAD request echoes the csrf_token
//    from the token exchange in X-CSRF-Token (session_middleware.go:75-80).
//  * Idempotency: every state-changing request carries an idempotency key,
//    both as `idempotency_key` in the JSON body (what request_payout,
//    submit_refund and the deposit intent read — advertiser.go:80-83,
//    394-398, publisher.go:246-250) and as X-Idempotency-Key (the transport
//    fallback internal/httpapi/middleware.go:32-48 accepts). Go's JSON
//    decoder ignores the extra field on endpoints that don't use it yet.
//    Callers pass the key for their user intent; retries reuse it.
//  * Step-up MFA: a 403 mfa_step_up_required (RequireRecentMFA,
//    session_middleware.go:97-110) or ErrStepUpRequired (auth.go:57)
//    triggers the registered prompt; on success the *same* request, with
//    the *same* idempotency key, is retried exactly once.
//  * Error mapping into ApiError (errors.ts).

export interface CsrfStore {
  get(): string | null;
  set(token: string | null): void;
}

const CSRF_KEY = "vads.csrf";

/**
 * The CSRF token lives in sessionStorage: per tab, gone when the tab
 * closes. It is not a bearer credential on its own (the session cookie is
 * httpOnly). A tab that has the cookie but no token (a new tab, a restored
 * one) re-fetches it from GET /v1/auth/session on load (G-02,
 * cmd/core-platform/account.go:144-155; AuthProvider loadSession).
 */
export const sessionCsrfStore: CsrfStore = {
  get: () => readStorage("session", CSRF_KEY),
  set: (token) => (token === null ? removeStorage("session", CSRF_KEY) : writeStorage("session", CSRF_KEY, token)),
};

export function memoryCsrfStore(initial: string | null = null): CsrfStore {
  let token = initial;
  return { get: () => token, set: (t) => (token = t) };
}

/** Resolves true if the user completed step-up MFA, false if they cancelled. */
export type StepUpHandler = () => Promise<boolean>;

export interface RequestOptions {
  signal?: AbortSignal;
  /**
   * Idempotency key for this user intent. Omit to get a fresh one (fine for
   * one-shot actions); pass a stable key when the caller may retry.
   * `null` sends none (auth ceremony endpoints).
   */
  idempotencyKey?: string | null;
  /** Don't intercept step-up (used by the step-up dialog's own calls). */
  noStepUp?: boolean;
  /**
   * Don't report a 401 to the session-lost handler. For calls that are
   * themselves the session check (GET /v1/auth/session, the unauthenticated
   * GET /v1/environment): their 401 means "not signed in", not "signed out
   * under you".
   */
  quiet?: boolean;
  /** Return the body as text (RawResponse) instead of parsing it. */
  raw?: boolean;
}

/** A successful response read as text (GET /v1/account/export: the JSON document is the download). */
export interface RawResponse {
  text: string;
  contentType: string | null;
  /** The filename from Content-Disposition, when the server names one. */
  filename: string | null;
}

export interface ApiClientOptions {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  csrf?: CsrfStore;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  readonly csrf: CsrfStore;
  private stepUpHandler: StepUpHandler | null = null;
  private stepUpInFlight: Promise<boolean> | null = null;
  private sessionLostHandler: ((err: ApiError) => void) | null = null;
  private accountClosedHandler: ((err: ApiError) => void) | null = null;

  constructor(opts: ApiClientOptions = {}) {
    this.baseUrl = opts.baseUrl ?? "";
    this.fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
    this.csrf = opts.csrf ?? sessionCsrfStore;
  }

  setStepUpHandler(handler: StepUpHandler | null): void {
    this.stepUpHandler = handler;
  }

  setSessionLostHandler(handler: ((err: ApiError) => void) | null): void {
    this.sessionLostHandler = handler;
  }

  /**
   * Migration 0065: once a party is closed, RequireSession answers every
   * route but the statements reads, /v1/me, the session re-issue, logout
   * and GET /v1/account/closure with 403 account_closed
   * (internal/auth/session_middleware.go:88-98, 114-125). The SQL's own
   * check raises the same code as a 422 (rejectStatus,
   * campaign_commands.go:219-225). Either way the handler switches the
   * portal to its closed-account screen.
   */
  setAccountClosedHandler(handler: ((err: ApiError) => void) | null): void {
    this.accountClosedHandler = handler;
  }

  get<T>(path: string, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("GET", path, undefined, opts);
  }

  post<T>(path: string, body?: Record<string, unknown> | object, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, body, opts);
  }

  put<T>(path: string, body?: Record<string, unknown> | object, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("PUT", path, body, opts);
  }

  patch<T>(path: string, body?: Record<string, unknown> | object, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("PATCH", path, body, opts);
  }

  delete<T>(path: string, body?: Record<string, unknown> | object, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("DELETE", path, body, opts);
  }

  /** GET whose successful body is kept as text, with its filename (a download). Errors are mapped as usual. */
  getRaw(path: string, opts: RequestOptions = {}): Promise<RawResponse> {
    return this.request<RawResponse>("GET", path, undefined, { ...opts, raw: true });
  }

  /** multipart/form-data POST (with CSRF, idempotency header and step-up like any other write). */
  upload<T>(path: string, form: FormData, opts: RequestOptions = {}): Promise<T> {
    return this.request<T>("POST", path, form, opts);
  }

  async request<T>(method: Method, path: string, body: unknown, opts: RequestOptions = {}): Promise<T> {
    const stateChanging = method !== "GET";
    // Fix the key once, before the first attempt, so a step-up retry reuses it.
    const idempotencyKey =
      stateChanging && opts.idempotencyKey !== null ? (opts.idempotencyKey ?? newIdempotencyKey()) : undefined;

    try {
      return await this.send<T>(method, path, body, idempotencyKey, opts.signal, opts.raw);
    } catch (err) {
      if (!(err instanceof ApiError) || err.kind !== "step_up_required" || opts.noStepUp || !this.stepUpHandler) {
        if (!opts.quiet) this.maybeSessionLost(err);
        throw err;
      }
      const completed = await this.promptStepUp();
      if (!completed) {
        throw new ApiError({
          kind: "step_up_required",
          status: err.status,
          code: err.code,
          idempotencyKey,
          path,
          message: "Verification was cancelled, so this action was not submitted.",
        });
      }
      try {
        return await this.send<T>(method, path, body, idempotencyKey, opts.signal, opts.raw);
      } catch (retryErr) {
        if (!opts.quiet) this.maybeSessionLost(retryErr);
        throw retryErr;
      }
    }
  }

  /** Several requests hitting step-up at once share one prompt. */
  private promptStepUp(): Promise<boolean> {
    if (!this.stepUpHandler) return Promise.resolve(false);
    if (!this.stepUpInFlight) {
      this.stepUpInFlight = this.stepUpHandler().finally(() => {
        this.stepUpInFlight = null;
      });
    }
    return this.stepUpInFlight;
  }

  private maybeSessionLost(err: unknown): void {
    if (err instanceof ApiError && (err.kind === "session" || err.kind === "csrf")) {
      this.sessionLostHandler?.(err);
    }
    if (err instanceof ApiError && err.code === "account_closed") {
      this.accountClosedHandler?.(err);
    }
  }

  private async send<T>(
    method: Method,
    path: string,
    body: unknown,
    idempotencyKey: string | undefined,
    signal: AbortSignal | undefined,
    raw = false,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: "application/json" };
    let payload: string | undefined;
    if (method !== "GET") {
      const csrf = this.csrf.get();
      if (csrf) headers["X-CSRF-Token"] = csrf;
      if (idempotencyKey) headers["X-Idempotency-Key"] = idempotencyKey;
    }
    let formBody: FormData | undefined;
    if (method !== "GET" && typeof FormData !== "undefined" && body instanceof FormData) {
      // Multipart (POST /v1/evidence): the browser sets the boundary, and
      // the key travels only in X-Idempotency-Key (dispute_evidence.go:193).
      formBody = body;
    } else if (method !== "GET") {
      let obj = body;
      if (idempotencyKey && (obj === undefined || (typeof obj === "object" && obj !== null && !Array.isArray(obj)))) {
        obj = { ...(obj as object | undefined), idempotency_key: idempotencyKey };
      }
      if (obj !== undefined) {
        headers["Content-Type"] = "application/json";
        payload = JSON.stringify(obj);
      }
    }

    let res: Response;
    try {
      res = await this.fetchImpl(this.baseUrl + path, {
        method,
        headers,
        body: formBody ?? payload,
        credentials: "same-origin",
        signal,
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      throw new ApiError({ kind: "network", idempotencyKey, path });
    }

    const text = await res.text();
    if (raw && res.ok) {
      const out: RawResponse = {
        text,
        contentType: res.headers.get("Content-Type"),
        filename: filenameFrom(res.headers.get("Content-Disposition")),
      };
      return out as T;
    }
    let data: unknown = undefined;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch {
        data = undefined;
      }
    }

    if (!res.ok) {
      const field = (name: string): string | undefined => {
        if (!data || typeof data !== "object") return undefined;
        const v = (data as Record<string, unknown>)[name];
        return typeof v === "string" ? v : undefined;
      };
      const code = field("error");
      const rawDetail = data && typeof data === "object" ? (data as Record<string, unknown>).detail : undefined;
      const detail =
        rawDetail && typeof rawDetail === "object" && !Array.isArray(rawDetail) ? (rawDetail as Record<string, unknown>) : undefined;
      throw new ApiError({
        // A plain-text detail: the staff commands' 409 request_rejected
        // {"detail": "<the SQL's own words>"} (provider_operations.go:209-214,
        // client_matrix.go:277-284) and safety.go:132-133's client_version_suspension.
        detailText: typeof rawDetail === "string" ? rawDetail : undefined,
        kind: kindFor(res.status, code),
        status: res.status,
        code,
        detail,
        reason: field("reason"),
        retryAfterSeconds: res.status === 429 ? parseRetryAfter(res.headers.get("Retry-After")) : undefined,
        idempotencyKey,
        path,
      });
    }
    return data as T;
  }
}

/** `attachment; filename="x.json"` → "x.json" (account_lifecycle.go:246 quotes it). */
export function filenameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const m = /filename="([^"]+)"/i.exec(disposition) ?? /filename=([^;\s]+)/i.exec(disposition);
  return m?.[1] ?? null;
}

/**
 * Retry-After is delta-seconds (what core-platform sends from
 * auth_ratelimit.go deny, always >= 1) or an HTTP-date. Returns whole
 * seconds, at least 1, or undefined when absent or unparseable.
 */
export function parseRetryAfter(value: string | null, now: number = Date.now()): number | undefined {
  if (!value) return undefined;
  const v = value.trim();
  if (/^\d+$/.test(v)) return Math.max(1, parseInt(v, 10));
  const at = Date.parse(v);
  if (Number.isNaN(at)) return undefined;
  return Math.max(1, Math.ceil((at - now) / 1000));
}

/** The app-wide client. Tests construct their own. */
export const api = new ApiClient();
